import {
  makeObservable,
  observable,
  action,
  runInAction,
  computed,
} from "mobx";
import ProjectPackagingOpsAPI from "../../api/ProjectPackagingOpsAPI";
import InventoryAPI from "../../api/InventoryAPI";
import {
  PackagingOpsItemRow,
  ProductSourcingSkuStatusInfo,
} from "../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import {
  InventoryAdjustCompleteRequest,
  InventoryAdjustRequest,
  InventoryInboundRequest,
  InventoryMovementLine,
  InventoryMovementRow,
  InventoryOrderSku,
  InventoryPackCreateRequest,
  InventoryPackItem,
  InventoryPackItemInput,
  InventoryPackSummary,
  InventoryPackUpdateRequest,
  InventoryVariant,
  InventoryVariantUpdateRequest,
} from "../../../../models/inventory/InventoryAdmin";
import { APIResponse } from "../../../../models/NetworkResponse";
import { PartnerCustomerInventoryTag } from "../../../../models/inventory/PartnerCustomerInventoryTag";
import PartnerCustomerInventoryTagsAPI from "../../api/PartnerCustomerInventoryTagsAPI";

export type PackagingOpsInwardLineMeta = {
  sku: string;
  clientSku: string;
  packSize?: number;
  boxSize?: string;
  tagId?: string;
};

const computeReadyQty = (row: ProductSourcingSkuStatusInfo): number => {
  if (row.backfill_allowed) {
    return row.order_quantity || 0;
  }
  return (
    (row.total_received_quantity || 0) - (row.total_rejected_quantity || 0)
  );
};

const buildItemRows = (
  sourcing: ProductSourcingSkuStatusInfo[],
  orderSkus: InventoryOrderSku[],
): PackagingOpsItemRow[] => {
  const orderSkuByKey = new Map<string, InventoryOrderSku>();
  orderSkus.forEach((orderSku) => {
    const catalogKey = orderSku.sku?.trim() || "";
    const clientKey = orderSku.clientSku?.trim() || "";
    if (catalogKey) {
      orderSkuByKey.set(catalogKey, orderSku);
    }
    if (clientKey && !orderSkuByKey.has(clientKey)) {
      orderSkuByKey.set(clientKey, orderSku);
    }
  });

  const rows: PackagingOpsItemRow[] = [];

  sourcing.forEach((s) => {
    const catalogSku = s.sku?.trim() || "";
    if (!catalogSku) return;
    const readyQty = computeReadyQty(s);
    const orderSku = orderSkuByKey.get(catalogSku);
    const clientSku = orderSku?.clientSku?.trim() || catalogSku;
    const contributedUnits = orderSku?.contributedUnits || 0;
    rows.push({
      sku: catalogSku,
      clientSku,
      productId: s.product_id ?? null,
      productName: s.product_name ?? null,
      title: s.title,
      readyQty,
      contributedUnits,
      onHand: orderSku?.onHand ?? null,
      reserved: orderSku?.reserved ?? null,
      free: orderSku?.free ?? null,
      remainingInward: readyQty - contributedUnits,
      backfillAllowed: !!s.backfill_allowed,
      packSize: orderSku?.packSize ?? null,
      boxSize: orderSku?.boxSize ?? null,
      tagId: orderSku?.tagId ?? null,
      tag: orderSku?.tag ?? null,
    });
  });

  return rows.sort((a, b) => a.sku.localeCompare(b.sku));
};

export class PackagingOpsStore {
  private packagingOpsAPI: ProjectPackagingOpsAPI;
  private inventoryAPI: InventoryAPI;
  private partnerCustomerInventoryTagsAPI: PartnerCustomerInventoryTagsAPI;

  sourcingBySku: ProductSourcingSkuStatusInfo[] = [];
  orderSkus: InventoryOrderSku[] = [];
  orderPacks: InventoryPackSummary[] = [];
  inventoryId: number | null = null;
  orderId: number | null = null;
  itemRows: PackagingOpsItemRow[] = [];
  customerTags: PartnerCustomerInventoryTag[] = [];

  constructor() {
    this.packagingOpsAPI = new ProjectPackagingOpsAPI();
    this.inventoryAPI = new InventoryAPI();
    this.partnerCustomerInventoryTagsAPI =
      new PartnerCustomerInventoryTagsAPI();

    makeObservable(this, {
      sourcingBySku: observable,
      orderSkus: observable,
      orderPacks: observable,
      inventoryId: observable,
      orderId: observable,
      itemRows: observable,
      customerTags: observable,
      totalRemainingInward: computed,
      load: action,
      loadCustomerTags: action,
      createInventory: action,
      createPack: action,
      patchPack: action,
      deletePack: action,
      replacePackItems: action,
      updateVariantMeta: action,
      deleteVariantBySku: action,
      adjustComplete: action,
      completeProject: action,
      reset: action,
    });
  }

  private reloadOrderPacks = async (projectId: number): Promise<void> => {
    const packsResponse = await this.inventoryAPI.getOrderPacks(projectId);
    runInAction(() => {
      this.orderPacks = packsResponse?.packs ?? [];
    });
  };

  get totalRemainingInward(): number {
    return this.itemRows.reduce((sum, row) => sum + row.remainingInward, 0);
  }

  reset = () => {
    this.sourcingBySku = [];
    this.orderSkus = [];
    this.orderPacks = [];
    this.orderId = null;
    this.itemRows = [];
    this.customerTags = [];
  };

  loadCustomerTags = async (customerId: number): Promise<void> => {
    try {
      if (!customerId) {
        runInAction(() => {
          this.customerTags = [];
        });
        return;
      }

      const tags =
        await this.partnerCustomerInventoryTagsAPI.getTagsByCustomerId(
          customerId,
        );
      runInAction(() => {
        this.customerTags = tags ?? [];
      });
    } catch (error) {
      throw error;
    }
  };

  load = async (projectId: number): Promise<void> => {
    try {
      const sourcing =
        await this.packagingOpsAPI.getSourcingInfoBySku(projectId);
      this.packagingOpsAPI.getSourcingInfoBySku(projectId);

      let orderSkus: InventoryOrderSku[] = [];
      let orderPacks: InventoryPackSummary[] = [];

      try {
        const packsResponse = await this.inventoryAPI.getOrderPacks(projectId);
        orderPacks = packsResponse?.packs ?? [];
        if (packsResponse?.inventoryId) {
          const orderSkusResponse =
            await this.inventoryAPI.getOrderSkus(projectId);
          orderSkus = orderSkusResponse?.skus ?? [];
          runInAction(() => {
            this.inventoryId = packsResponse.inventoryId;
          });
        }
      } catch {
        // No inventory / packs yet — leave empty
      }

      runInAction(() => {
        this.orderId = projectId;
        this.sourcingBySku = sourcing ?? [];
        this.orderSkus = orderSkus;
        this.orderPacks = orderPacks;
        this.itemRows = buildItemRows(this.sourcingBySku, this.orderSkus);
      });
    } catch (error) {
      throw error;
    }
  };

  createInventory = async (
    customerId: number,
    label: string,
    orderId: number,
    type: number,
  ): Promise<void> => {
    try {
      await this.packagingOpsAPI.createInventory(
        customerId,
        label,
        orderId,
        type,
      );
      await this.load(orderId);
    } catch (error) {
      throw error;
    }
  };

  inwardItems = async (
    projectId: number,
    rows: PackagingOpsItemRow[],
    lineMeta: PackagingOpsInwardLineMeta[] = [],
  ): Promise<void> => {
    try {
      const metaByCatalogSku = new Map(
        lineMeta.map((meta) => [meta.sku.trim(), meta]),
      );

      const lines: InventoryMovementLine[] = rows
        .filter(
          (row) =>
            row.remainingInward > 0 && row.sku?.trim() && row.productId != null,
        )
        .map((row) => {
          const meta = metaByCatalogSku.get(row.sku.trim());
          const clientSku = meta?.clientSku?.trim() || row.clientSku;
          const line: InventoryMovementLine = {
            clientSku,
            sku: row.sku,
            qty: row.remainingInward,
            productId: row.productId ?? undefined,
          };

          if (meta?.packSize != null && Number.isFinite(meta.packSize)) {
            line.packSize = meta.packSize;
          }
          if (meta?.boxSize?.trim()) {
            line.boxSize = meta.boxSize.trim();
          }
          if (meta?.tagId?.trim()) {
            line.tagId = meta.tagId.trim();
          }

          return line;
        })
        .filter((line) => line.clientSku.trim().length > 0);

      if (lines.length === 0) {
        throw new Error("No eligible SKUs selected for inward");
      }

      const payload: InventoryInboundRequest = {
        orderId: projectId,
        lines,
      };

      const result = await this.inventoryAPI.inbound(payload);
      runInAction(() => {
        if (result?.inventoryId) {
          this.inventoryId = result.inventoryId;
        }
      });
      await this.load(projectId);
    } catch (error) {
      throw error;
    }
  };

  updateVariantMeta = async (
    projectId: number,
    clientSku: string,
    payload: InventoryVariantUpdateRequest,
  ): Promise<void> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Please create or map inventory first");
      }

      const variant = await this.inventoryAPI.getVariantBySku(
        this.inventoryId,
        clientSku,
      );
      if (!variant?.id) {
        throw new Error("Variant not found for this SKU");
      }

      await this.inventoryAPI.updateVariant(variant.id, payload);
      await this.load(projectId);
    } catch (error) {
      throw error;
    }
  };

  deleteVariantBySku = async (
    projectId: number,
    clientSku: string,
  ): Promise<void> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Please create or map inventory first");
      }
      const trimmedSku = clientSku.trim();
      if (!trimmedSku) {
        throw new Error("Client SKU is required");
      }
      await this.inventoryAPI.deleteVariantBySku(this.inventoryId, trimmedSku);
      await this.load(projectId);
    } catch (error) {
      throw error;
    }
  };

  adjustItem = async (
    projectId: number,
    payload: InventoryAdjustRequest,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.adjust(payload);
      await this.load(projectId);
    } catch (error) {
      throw error;
    }
  };

  adjustComplete = async (
    projectId: number,
    payload: InventoryAdjustCompleteRequest,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.adjustComplete(payload);
      await this.load(projectId);
    } catch (error) {
      throw error;
    }
  };

  createPack = async (
    projectId: number,
    payload: Omit<InventoryPackCreateRequest, "inventoryId">,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.createPack({
        ...payload,
        inventoryId: this.inventoryId,
        orderId: projectId,
      });

      await this.reloadOrderPacks(projectId);
    } catch (error) {
      throw error;
    }
  };

  getPackDetails = async (packId: number): Promise<InventoryPackItem[]> => {
    try {
      const pack = await this.inventoryAPI.getPack(packId);
      return pack?.items ?? [];
    } catch (error) {
      throw error;
    }
  };

  patchPack = async (
    projectId: number,
    packId: number,
    payload: InventoryPackUpdateRequest,
  ): Promise<void> => {
    try {
      await this.inventoryAPI.patchPack(packId, payload);
      await this.reloadOrderPacks(projectId);
    } catch (error) {
      throw error;
    }
  };

  deletePack = async (projectId: number, packId: number): Promise<void> => {
    try {
      await this.patchPack(projectId, packId, { active: 0 });
    } catch (error) {
      throw error;
    }
  };

  searchVariants = async (search: string): Promise<InventoryVariant[]> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Please create or map inventory first");
      }

      const response = await this.inventoryAPI.getVariants({
        inventoryId: this.inventoryId,
        search: search.trim() || undefined,
      });

      return response?.variants ?? [];
    } catch (error) {
      throw error;
    }
  };

  replacePackItems = async (
    projectId: number,
    packId: number,
    items: InventoryPackItemInput[],
  ): Promise<void> => {
    try {
      await this.inventoryAPI.replaceBom(packId, { items });
      await this.reloadOrderPacks(projectId);
    } catch (error) {
      throw error;
    }
  };

  getSkuMovements = async (
    clientSku: string,
    limit = 50,
  ): Promise<InventoryMovementRow[]> => {
    try {
      if (!this.inventoryId) {
        throw new Error("Please create or map inventory first");
      }

      const response = await this.inventoryAPI.getMovements({
        inventoryId: this.inventoryId,
        clientSku,
        limit,
        cursor: 0,
      });

      return response.rows ?? [];
    } catch (error) {
      throw error;
    }
  };

  completeProject = async (projectId: number): Promise<APIResponse> => {
    try {
      return await this.packagingOpsAPI.completeProject(projectId);
    } catch (error) {
      throw error;
    }
  };
}
